import { PropsWithChildren, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useKey } from 'react-use'
import gsap from 'gsap'

import SVGIcon from '@/components/base/svg-icon'

import { cn } from '@/lib/utils'

type ModalProps = PropsWithChildren<{
  open: boolean
  onClose: () => void
  className?: string
  closeLabel?: string
}>

const Modal = ({
  open,
  onClose,
  className,
  closeLabel = 'Chiudi',
  children
}: ModalProps) => {
  const [mounted, setMounted] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  // Portal mount
  useEffect(() => {
    setMounted(true)
  }, [])

  // GSAP open/close animation
  useEffect(() => {
    if (!rootRef.current) return

    gsap.to(rootRef.current, {
      opacity: open ? 1 : 0,
      pointerEvents: open ? 'auto' : 'none',
      duration: 0.3,
      ease: 'power2.out'
    })
  }, [open])

  // Lock body scroll
  useEffect(() => {
    if (!open) return

    const prevBodyOverflow = document.body.style.overflow
    const prevHtmlOverflow = document.documentElement.style.overflow

    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = prevBodyOverflow
      document.documentElement.style.overflow = prevHtmlOverflow
    }
  }, [open])

  // Focus trap: focus close button on open
  useEffect(() => {
    if (open && closeButtonRef.current) {
      closeButtonRef.current.focus()
    }
  }, [open])

  // Escape key
  useKey(
    'Escape',
    () => {
      if (open) onClose()
    },
    {},
    [open, onClose]
  )

  if (!mounted) return null

  return createPortal(
    <div
      ref={rootRef}
      className={cn(
        'pointer-events-none fixed inset-0 z-[110] flex h-[calc(100*var(--lvh))] items-center justify-center bg-[black]/90 opacity-0',
        className
      )}
      role="dialog"
      aria-modal="true"
      aria-hidden={!open}
    >
      <button
        ref={closeButtonRef}
        type="button"
        data-modal-close
        aria-label={closeLabel}
        onClick={onClose}
        className={cn(
          'absolute z-30 flex size-[60px] items-center justify-center rounded-full border border-border-secondary-dark/20 bg-[black]/20 text-[white] backdrop-blur-[17px]',
          'right-[50%] top-[10px] translate-x-1/2 scale-[0.8] md:right-[19px] md:top-[19px] md:translate-x-0 md:scale-100'
        )}
      >
        <SVGIcon symbol="close" className="size-[16px]" />
      </button>

      {open ? children : null}
    </div>,
    document.body
  )
}

export default Modal
